import { QueryClient, type DefaultOptions } from '@tanstack/react-query';
import { isApiError } from '../api';
import { errorService } from './error-service';

// Default query options with optimal caching settings and enhanced error handling
const defaultOptions: DefaultOptions = {
  queries: {
    // Cache data for 5 minutes before considering it stale
    staleTime: 5 * 60 * 1000,
    // Keep data in cache for 15 minutes after component unmounts (increased for better UX)
    gcTime: 15 * 60 * 1000,
    // Retry failed requests with exponential backoff
    retry: (failureCount, error) => {
      // Don't retry on client errors (4xx) except for network errors
      if (isApiError(error)) {
        if (error.isClientError && !error.isNetworkError) {
          return false;
        }
        // Don't retry on authentication/authorization errors
        if (error.isUnauthorized || error.isForbidden) {
          return false;
        }
      }
      // Retry up to 3 times for other errors
      return failureCount < 3;
    },
    // Exponential backoff delay
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    // Refetch on window focus for important data (enabled for better data freshness)
    refetchOnWindowFocus: true,
    // Refetch on reconnect for data consistency
    refetchOnReconnect: 'always',
    // Enable background refetching for better UX
    refetchInterval: false, // Can be overridden per query
    // Network mode for better offline handling
    networkMode: 'online',
  },
  mutations: {
    // Retry mutations once on network errors
    retry: (failureCount, error) => {
      if (isApiError(error) && error.isNetworkError) {
        return failureCount < 1;
      }
      return false;
    },
    // Enhanced global error handling for mutations using error service
    onError: (error, variables, context) => {
      // Use the error service for consistent error handling
      errorService.handleError(error, {
        component: 'Mutation',
        action: 'unknown',
        metadata: { variables, context },
      }, {
        // Don't show toast for validation errors (they should be handled by forms)
        showToast: !isApiError(error) || !error.isValidationError,
      });
    },
  },
};

// Create QueryClient instance with optimized settings
export const queryClient = new QueryClient({
  defaultOptions,
});

// Query key factories for consistent cache management
export const queryKeys = {
  // Auth queries
  auth: {
    currentUser: () => ['auth', 'currentUser'] as const,
  },
  
  // User queries
  users: {
    all: () => ['users'] as const,
    lists: () => [...queryKeys.users.all(), 'list'] as const,
    list: (params?: any) => 
      [...queryKeys.users.lists(), params] as const,
    details: () => [...queryKeys.users.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
  },
  
  // Role queries
  roles: {
    all: () => ['roles'] as const,
    lists: () => [...queryKeys.roles.all(), 'list'] as const,
    list: (params?: any) => 
      [...queryKeys.roles.lists(), params] as const,
    details: () => [...queryKeys.roles.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.roles.details(), id] as const,
    usage: (id: string) => [...queryKeys.roles.all(), 'usage', id] as const,
  },
  
  // Permission queries
  permissions: {
    all: () => ['permissions'] as const,
  },
} as const;

// Utility functions for cache management
export const cacheUtils = {
  // Invalidate all user-related queries
  invalidateUsers: () => {
    return queryClient.invalidateQueries({ queryKey: queryKeys.users.all() });
  },
  
  // Invalidate all role-related queries
  invalidateRoles: () => {
    return queryClient.invalidateQueries({ queryKey: queryKeys.roles.all() });
  },
  
  // Invalidate specific user
  invalidateUser: (id: string) => {
    return queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(id) });
  },
  
  // Invalidate specific role
  invalidateRole: (id: string) => {
    return queryClient.invalidateQueries({ queryKey: queryKeys.roles.detail(id) });
  },

  // Prefetch user details for better UX
  prefetchUser: (id: string) => {
    return queryClient.prefetchQuery({
      queryKey: queryKeys.users.detail(id),
      queryFn: async () => {
        const { userApi } = await import('../api');
        return userApi.getUser(id);
      },
      staleTime: 5 * 60 * 1000,
    });
  },

  // Prefetch role details for better UX
  prefetchRole: (id: string) => {
    return queryClient.prefetchQuery({
      queryKey: queryKeys.roles.detail(id),
      queryFn: async () => {
        const { roleApi } = await import('../api');
        return roleApi.getRole(id);
      },
      staleTime: 10 * 60 * 1000,
    });
  },

  // Optimistically add user to cache
  addUserToCache: (user: any) => {
    queryClient.setQueryData(queryKeys.users.detail(user.id), user);
    
    // Add to lists
    queryClient.setQueriesData(
      { queryKey: queryKeys.users.lists() },
      (oldData: any) => {
        if (!oldData?.items) return oldData;
        
        return {
          ...oldData,
          items: [user, ...oldData.items],
          total: oldData.total + 1,
        };
      }
    );
  },

  // Optimistically add role to cache
  addRoleToCache: (role: any) => {
    queryClient.setQueryData(queryKeys.roles.detail(role.id), role);
    
    // Add to lists
    queryClient.setQueriesData(
      { queryKey: queryKeys.roles.lists() },
      (oldData: any) => {
        if (!oldData?.items) return oldData;
        
        return {
          ...oldData,
          items: [role, ...oldData.items],
          total: oldData.total + 1,
        };
      }
    );
  },
  
  // Update user in cache optimistically
  updateUserInCache: (id: string, updater: (oldData: any) => any) => {
    queryClient.setQueryData(queryKeys.users.detail(id), updater);
    
    // Also update the user in any lists that might contain it
    queryClient.setQueriesData(
      { queryKey: queryKeys.users.lists() },
      (oldData: any) => {
        if (!oldData?.items) return oldData;
        
        return {
          ...oldData,
          items: oldData.items.map((user: any) => 
            user.id === id ? updater(user) : user
          ),
        };
      }
    );
  },
  
  // Update role in cache optimistically
  updateRoleInCache: (id: string, updater: (oldData: any) => any) => {
    queryClient.setQueryData(queryKeys.roles.detail(id), updater);
    
    // Also update the role in any lists that might contain it
    queryClient.setQueriesData(
      { queryKey: queryKeys.roles.lists() },
      (oldData: any) => {
        if (!oldData?.items) return oldData;
        
        return {
          ...oldData,
          items: oldData.items.map((role: any) => 
            role.id === id ? updater(role) : role
          ),
        };
      }
    );
  },
  
  // Remove user from cache
  removeUserFromCache: (id: string) => {
    queryClient.removeQueries({ queryKey: queryKeys.users.detail(id) });
    
    // Remove from lists
    queryClient.setQueriesData(
      { queryKey: queryKeys.users.lists() },
      (oldData: any) => {
        if (!oldData?.items) return oldData;
        
        return {
          ...oldData,
          items: oldData.items.filter((user: any) => user.id !== id),
          total: Math.max(0, oldData.total - 1),
        };
      }
    );
  },
  
  // Remove role from cache
  removeRoleFromCache: (id: string) => {
    queryClient.removeQueries({ queryKey: queryKeys.roles.detail(id) });
    
    // Remove from lists
    queryClient.setQueriesData(
      { queryKey: queryKeys.roles.lists() },
      (oldData: any) => {
        if (!oldData?.items) return oldData;
        
        return {
          ...oldData,
          items: oldData.items.filter((role: any) => role.id !== id),
          total: Math.max(0, oldData.total - 1),
        };
      }
    );
  },
};